import { useState } from "react";
import { Database, X } from "lucide-react";

import type { PlatformChurchDetail } from "@/api/platform";
import { useSeedPlatformChurch } from "@/features/platform/hooks/usePlatformChurches";

type Props = {
  church: PlatformChurchDetail;
  onViewOverview?: () => void;
  onViewInvitation?: () => void;
};

export function ChurchOperationsPanel({ church, onViewOverview, onViewInvitation }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const seedMutation = useSeedPlatformChurch(church.church.id);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-slate-950">Operations</h3>
        <p className="mt-1 text-sm text-slate-500">Platform management actions for this church.</p>
      </div>

      <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5 text-indigo-600" />
              <h4 className="font-semibold text-slate-950">Demonstration data</h4>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              Populate an operationally empty church with 150 members, 40 households,
              26 visitors, two service schedules, and six months of attendance history.
            </p>
          </div>
          <button type="button" onClick={() => { seedMutation.reset(); setIsOpen(true); }} className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500">Seed Church</button>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-labelledby="seed-church-title">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 id="seed-church-title" className="text-xl font-semibold text-slate-950">Seed {church.church.name}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">This creates fictional demonstration data. Provisioning records and invitations are preserved, and the church lifecycle is not changed.</p>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} disabled={seedMutation.isPending} aria-label="Close" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            {seedMutation.data && (
              <div className="mt-5 rounded-xl bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-emerald-950">Demonstration data was created successfully.</p>
                <p className="mt-1 text-sm leading-6 text-emerald-800">
                  {church.church.status === "PENDING_ACTIVATION"
                    ? "The church remains pending until the initial administrator accepts the invitation and completes activation."
                    : "The church lifecycle status was not changed by seeding."}
                </p>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  {[
                    ["Households", seedMutation.data.households_created],
                    ["Members", seedMutation.data.members_created],
                    ["Visitors", seedMutation.data.visitors_created],
                    ["Schedules", seedMutation.data.schedules_created],
                    ["Occurrences", seedMutation.data.occurrences_created],
                    ["Attendance", seedMutation.data.attendance_created],
                    ["Care items", seedMutation.data.care_items_created],
                  ].map(([label, value]) => <div key={label}><dt className="text-emerald-700">{label}</dt><dd className="font-semibold text-emerald-950">{value}</dd></div>)}
                </dl>
              </div>
            )}

            {seedMutation.isError && <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{seedMutation.error instanceof Error ? seedMutation.error.message : "Demo data generation failed. No demo records were saved."}</p>}

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => setIsOpen(false)} disabled={seedMutation.isPending} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">{seedMutation.data ? "Close" : "Cancel"}</button>
              {seedMutation.data && <button type="button" onClick={() => { setIsOpen(false); onViewInvitation?.(); }} className="rounded-lg border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-700">View Invitation</button>}
              {seedMutation.data && <button type="button" onClick={() => { setIsOpen(false); onViewOverview?.(); }} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">View Overview</button>}
              {!seedMutation.data && <button type="button" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-60">{seedMutation.isPending ? "Seeding church..." : "Seed Church"}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
